import { useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Undo, Redo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Simple Rich Text Editor con formattazione base
 * Supporta: grassetto, corsivo, link, liste
 */
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Scrivi qui...',
  className = '',
  minHeight = '150px',
  disabled = false
}) {
  const editorRef = useRef(null);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    // Trigger onChange
    if (onChange) {
      onChange(editorRef.current?.innerHTML || '');
    }
  }, [onChange]);

  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  
  const handleLink = () => {
    const url = prompt('Inserisci URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };
  
  const handleUnorderedList = () => execCommand('insertUnorderedList');
  const handleOrderedList = () => execCommand('insertOrderedList');
  const handleUndo = () => execCommand('undo');
  const handleRedo = () => execCommand('redo');

  const handleInput = () => {
    if (onChange) {
      onChange(editorRef.current?.innerHTML || '');
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className={cn('border border-input rounded-sm', className)}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 border-b border-input bg-muted/30">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBold}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Grassetto (Ctrl+B)"
        >
          <Bold size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleItalic}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Corsivo (Ctrl+I)"
        >
          <Italic size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleLink}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Inserisci Link"
        >
          <LinkIcon size={16} />
        </Button>
        
        <div className="w-px h-6 bg-border mx-1 self-center" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleUnorderedList}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Elenco puntato"
        >
          <List size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleOrderedList}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Elenco numerato"
        >
          <ListOrdered size={16} />
        </Button>
        
        <div className="w-px h-6 bg-border mx-1 self-center" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleUndo}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Annulla (Ctrl+Z)"
        >
          <Undo size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRedo}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Ripeti (Ctrl+Y)"
        >
          <Redo size={16} />
        </Button>
      </div>
      
      {/* Editor Content */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onPaste={handlePaste}
        className={cn(
          'p-3 focus:outline-none prose prose-sm max-w-none',
          'prose-p:my-2 prose-ul:my-2 prose-ol:my-2',
          'prose-a:text-primary prose-a:underline',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{ minHeight }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || '') }}
        data-placeholder={placeholder}
      />
      
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: hsl(var(--muted-foreground));
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
